/**
 * TrashGuardModal
 *
 * Blocking confirmation shown when a trash request hit the server's
 * trash guard (409 `trash_guard`): the selection contains order-related
 * or currently-Important emails, which must never be hidden silently.
 *
 * The dialog lists the SPECIFIC guarded emails (subject / sender / date /
 * reason badge) — per the owner's direction, no guess-and-check. Actions:
 *
 *   - Cancel (default, focused) — trash nothing.
 *   - "Skip these, trash the rest" — bulk only, when the selection also
 *     contains unguarded emails: re-request with the unguarded subset.
 *   - "Trash anyway" — re-request with the confirm flag; everything,
 *     guarded included, is trashed.
 *
 * Esc and Enter both trigger Cancel (the safe default).
 */

import { useEffect, useRef } from 'react';
import {
  GUARD_REASON_LABEL,
  type GuardedTrashEmail,
} from '../../utils/trashGuard';
import styles from './TrashGuardModal.module.css';

export interface TrashGuardModalProps {
  guarded: GuardedTrashEmail[];
  /** Size of the whole trash request (bulk selection, or 1 for single). */
  totalSelected: number;
  onCancel: () => void;
  /** Trash everything, guarded included (confirm-flag retry). */
  onTrashAnyway: () => void;
  /** Trash only the unguarded remainder. Omit for single-email trash. */
  onSkipGuarded?: () => void;
  /** Disables all buttons while the parent re-submits. */
  busy?: boolean;
}

function formatReceived(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** Sender display: prefer the name part of `"Name" <addr>`, else the addr. */
function formatSender(fromAddress: string | null): string {
  if (!fromAddress) return '';
  const match = fromAddress.match(/^\s*"?([^"<]+?)"?\s*</);
  return (match ? match[1] : fromAddress).trim();
}

export default function TrashGuardModal({
  guarded,
  totalSelected,
  onCancel,
  onTrashAnyway,
  onSkipGuarded,
  busy = false,
}: TrashGuardModalProps) {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  const skipCount = totalSelected - guarded.length;
  const showSkip = Boolean(onSkipGuarded) && skipCount > 0;

  // Focus the safe default on mount.
  useEffect(() => {
    cancelBtnRef.current?.focus();
  }, []);

  // Esc and Enter both cancel — trashing must be an explicit click.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (busy) return;
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  const headline =
    guarded.length === 1
      ? 'This email looks like order-related work. Trashing hides it from every inbox view.'
      : `${guarded.length} of the ${totalSelected} selected emails look like order-related work. Trashing hides them from every inbox view.`;

  return (
    <div
      className={styles.overlay}
      // No close-on-overlay-click — the user must make an explicit choice.
      role="dialog"
      aria-modal="true"
      aria-labelledby="trashGuardTitle"
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div className={styles.icon} aria-hidden="true">
            !
          </div>
          <div className={styles.title} id="trashGuardTitle">
            {guarded.length === 1
              ? 'Trash an order-related email?'
              : `Trash ${guarded.length} order-related emails?`}
          </div>
        </div>

        <div className={styles.body}>
          <p className={styles.lede}>{headline}</p>
          <ul className={styles.itemList}>
            {guarded.map((g) => (
              <li
                key={g.id}
                className={styles.item}
                data-testid={`trash-guard-item-${g.id}`}
              >
                <span
                  className={
                    g.reason === 'order'
                      ? `${styles.badge} ${styles.badgeOrder}`
                      : `${styles.badge} ${styles.badgeImportant}`
                  }
                >
                  {GUARD_REASON_LABEL[g.reason]}
                </span>
                <span className={styles.subject}>{g.subject}</span>
                <span className={styles.meta}>
                  {formatSender(g.from_address)}
                  {g.received_at ? ` · ${formatReceived(g.received_at)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            ref={cancelBtnRef}
            className={`${styles.btn} ${styles.btnCancel}`}
            onClick={onCancel}
            disabled={busy}
            data-testid="trash-guard-cancel"
          >
            Cancel
          </button>
          {showSkip && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSkip}`}
              onClick={onSkipGuarded}
              disabled={busy}
              data-testid="trash-guard-skip"
            >
              Skip these, trash the other {skipCount}
            </button>
          )}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnTrash}`}
            onClick={onTrashAnyway}
            disabled={busy}
            data-testid="trash-guard-anyway"
          >
            Trash anyway
          </button>
        </div>
      </div>
    </div>
  );
}
